// Equipment (decision 0053: "the switchable weapon plus two charm slots"). Slots are the game's data: each has a kind
// and holds one item of that kind (`ItemDef.slot`). An equipped item stays in the inventory (a weapon tab shows it
// marked); the slot names it, and a slot may hold an item only while the inventory holds more of it than the other
// slots do. Forge levels are kept here by item id (spike 002's items are unique and fixed, 0053). The equipped items'
// modifiers, at their levels, reach a character's ability state as infinite effects owned by "equip" (one per slot),
// reconciled with `AbilitySet.sync`.
import type { AbilitySet, AbilityState, EffectDef } from "@rpg/abilities";
import type { Inventory, InventoryState } from "./inventory";
import type { ItemTable } from "./items";

/** One equipment slot. */
export interface SlotDef {
  id: string;
  /** The item slot kind it takes (`ItemDef.slot`). */
  kind: string;
}

/** What is equipped, and each item's forge level. Plain JSON. */
export interface EquipmentState {
  slots: Record<string, string | null>;
  /** Forge levels by item id (absent: 1). */
  levels: Record<string, number>;
}

/** Why an item can't go in a slot. */
export type EquipRefusal = "slot" | "unknown" | "kind" | "unowned";

/** The owner id of the effects equipment puts on a character. */
export const EQUIP_OWNER = "equip";

/** Equipment over the item table and the game's slots. */
export interface Equipment {
  readonly slots: readonly SlotDef[];
  /** A state with every slot empty (or holding `start`'s items). */
  init(start?: Readonly<Record<string, string | null>>): EquipmentState;
  canEquip(eq: EquipmentState, inv: InventoryState, slot: string, item: string): EquipRefusal | null;
  /** Put an item in a slot (what was there goes back to the inventory, which it never left). */
  equip(eq: EquipmentState, inv: InventoryState, slot: string, item: string): EquipRefusal | null;
  unequip(eq: EquipmentState, slot: string): void;
  /** How many slots hold an item (a shop may not buy those). */
  equipped(eq: EquipmentState, item: string): number;
  level(eq: EquipmentState, item: string): number;
  /** The effects the equipped items imply, as `AbilitySet.sync` takes them. */
  effects(eq: EquipmentState): { def: EffectDef }[];
  /** Make a character's ability state carry exactly the equipped items' modifiers. */
  sync(set: AbilitySet, stats: AbilityState, eq: EquipmentState): void;
  /** Problems with a state: unknown slots, items of the wrong kind, items the inventory no longer holds enough of,
   *  levels outside the item's range. */
  check(eq: EquipmentState, inv: InventoryState): string[];
}

/** Build equipment over the item table and the slots. Throws on a duplicate slot id. */
export function createEquipment(items: ItemTable, inventory: Inventory, slots: readonly SlotDef[]): Equipment {
  const byId = new Map<string, SlotDef>();
  for (const s of slots) {
    if (byId.has(s.id)) throw new Error(`equipment: slot "${s.id}" defined twice`);
    byId.set(s.id, s);
  }
  const equipped = (eq: EquipmentState, item: string, except?: string): number => Object.entries(eq.slots).filter(([k, v]) => v === item && k !== except).length;
  // effect definitions cached by slot, item and level (sync compares by identity)
  const cache = new Map<string, EffectDef>();
  const defFor = (slot: string, item: string, level: number): EffectDef => {
    const key = `${slot}|${item}|${level}`;
    let d = cache.get(key);
    if (!d) {
      d = { id: `${EQUIP_OWNER}:${slot}`, duration: "infinite", modifiers: items.modifiersAt(item, level), grants: [`equipped.${slot}`] };
      cache.set(key, d);
    }
    return d;
  };
  const e: Equipment = {
    slots,
    init(start = {}) {
      const st: EquipmentState = { slots: Object.fromEntries(slots.map((s) => [s.id, null])), levels: {} };
      for (const [k, v] of Object.entries(start)) {
        if (!byId.has(k)) throw new Error(`equipment: no slot "${k}"`);
        st.slots[k] = v;
      }
      return st;
    },
    canEquip(eq, inv, slot, item) {
      const s = byId.get(slot);
      if (!s) return "slot";
      if (!items.has(item)) return "unknown";
      if (items.get(item).slot !== s.kind) return "kind";
      if (inventory.count(inv, item) <= equipped(eq, item, slot)) return "unowned";
      return null;
    },
    equip(eq, inv, slot, item) {
      const why = e.canEquip(eq, inv, slot, item);
      if (why) return why;
      eq.slots[slot] = item;
      return null;
    },
    unequip(eq, slot) {
      if (byId.has(slot)) eq.slots[slot] = null;
    },
    equipped: (eq, item) => equipped(eq, item),
    level: (eq, item) => eq.levels[item] ?? 1,
    effects(eq) {
      const out: { def: EffectDef }[] = [];
      for (const s of slots) {
        const item = eq.slots[s.id];
        if (!item || !items.has(item)) continue;
        out.push({ def: defFor(s.id, item, e.level(eq, item)) });
      }
      return out;
    },
    sync: (set, stats, eq) => set.sync(stats, EQUIP_OWNER, e.effects(eq)),
    check(eq, inv) {
      const out: string[] = [];
      for (const [k, v] of Object.entries(eq.slots)) {
        const s = byId.get(k);
        if (!s) {
          out.push(`unknown slot "${k}"`);
          continue;
        }
        if (v === null) continue;
        if (!items.has(v)) {
          out.push(`slot "${k}" holds unknown item "${v}"`);
          continue;
        }
        if (items.get(v).slot !== s.kind) out.push(`slot "${k}" holds "${v}", not a ${s.kind}`);
      }
      const counts = new Map<string, number>();
      for (const v of Object.values(eq.slots)) if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
      for (const [item, n] of counts) if (inventory.count(inv, item) < n) out.push(`"${item}" is equipped ${n} times but held ${inventory.count(inv, item)}`);
      for (const [item, l] of Object.entries(eq.levels)) if (!items.has(item) || !Number.isInteger(l) || l < 1 || l > items.maxLevel(item)) out.push(`"${item}" at level ${l}`);
      return out;
    },
  };
  return e;
}
